import React, {createContext, useContext, useCallback, useMemo} from "react";
import {RazorpayClient} from "./razorpay-client";
import {RazorpayGlobalOptions} from "./@types/RazorpayGlobalOptions";
import {RazorpayPaymentOptions} from "./@types/RazorpayPaymentOptions";

/**
 * Context to store Razorpay payment options.
 */
const RazorpayContext = createContext<RazorpayGlobalOptions | null>(null);

/**
 * Provides Razorpay configuration to child components.
 * @param options - Default payment options for Razorpay.
 * @param children - React child components.
 */
export function RazorpayProvider({
  options,
  children,
}: Readonly<{
  options: RazorpayGlobalOptions;
  children: React.ReactNode;
}>) {
  if (!options.key) {
    throw new Error("RazorpayProvider must be provided with a key.");
  }

  return (
    <RazorpayContext.Provider value={options}>
      {children}
    </RazorpayContext.Provider>
  );
}

/**
 * Hook to trigger a Razorpay payment with optional global configuration overrides.
 * @param overrideConfig - Optional global overrides (e.g., theme, config)
 * @returns A function that accepts payment-specific fields and opens Razorpay Checkout.
 */
export function useRazorpayFromProvider(
  overrideConfig?: Partial<RazorpayGlobalOptions>
) {
  const isClient = useMemo(() => typeof window !== "undefined", []);
  const providerOptions = useContext(RazorpayContext);

  if (!providerOptions) {
    throw new Error("useRazorpay must be used within a RazorpayProvider");
  }

  return useCallback(
    async (paymentOptions: RazorpayPaymentOptions) => {
      if (!isClient) return;
      await RazorpayClient.openPayment({
        ...providerOptions,
        ...overrideConfig,
        ...paymentOptions,
      });
    },
    [providerOptions]
  );
}
